import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLandingArticles, isLandingStale } from '../slices/landingSlice.mjs';

/**
 * Dedicated hook for landing page article data.
 *
 * Reads from state.landing (NOT state.unifiedArticles) so landing page
 * articles are fully independent from category/topic infrastructure.
 *
 * Features:
 *   - SSR hydration guard (skips fetch if data already in store from preloadedState)
 *   - Automatic staleness detection (5-minute TTL)
 *   - No pagination — landing page is a curated preview
 *
 * @returns {{ articles: Array, isLoading: boolean, error: string|null, refetch: Function }}
 */
export const useLandingArticles = () => {
    const dispatch = useDispatch();

    // SSR hydration guard: skip fetch on first effect if data already exists
    const hasHydrated = useRef(false);

    const { articles, loading, error, fetchedAt } = useSelector(
        (state) => state.landing ?? { articles: [], loading: false, error: null, fetchedAt: null }
    );

    useEffect(() => {
        // SSR hydration guard: if data already exists on first mount, trust it
        if (!hasHydrated.current) {
            hasHydrated.current = true;
            if (articles.length > 0 && fetchedAt) {
                return;
            }
        }

        // Skip if already loading
        if (loading) return;

        // Skip if data is fresh
        if (articles.length > 0 && !isLandingStale(fetchedAt)) return;

        // Fetch fresh data
        dispatch(fetchLandingArticles());
    }, [dispatch, articles.length, loading, fetchedAt]);

    const refetch = () => {
        dispatch(fetchLandingArticles());
    };

    return {
        articles,
        isLoading: loading,
        error,
        refetch,
    };
};

export default useLandingArticles;
